import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  FiAlertCircle,
  FiChevronDown,
  FiChevronRight,
  FiFolder,
  FiMove,
  FiX,
} from "react-icons/fi";
import {
  BulkMoveMediaDocument,
  type BulkMoveMediaMutation,
  type BulkMoveMediaMutationVariables,
  GetFoldersDocument,
  type GetFoldersQuery,
  type GetFoldersQueryVariables,
  GetMediaDocument,
  type MediaType,
} from "../../../generated/graphql";

interface MoveMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaIds: string[];
  mediaType: MediaType;
  currentFolderId: string | null;
}

interface FolderTreeNode {
  id: string;
  name: string;
  expanded: boolean;
}

function FolderTreeItem({
  folder,
  selectedId,
  onSelect,
  onToggle,
  mediaType,
  currentFolderId,
  expandedFolders,
}: {
  folder: FolderTreeNode;
  selectedId: string | null | undefined;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  mediaType: MediaType;
  currentFolderId: string | null;
  expandedFolders: Set<string>;
}) {
  // Always query child folders to know if we should show expand arrow
  const { data } = useQuery<GetFoldersQuery, GetFoldersQueryVariables>(GetFoldersDocument, {
    variables: { parentId: folder.id, type: mediaType },
  });

  const childFolders = data?.folders?.edges?.map((e) => e.node) || [];
  const hasChildren = childFolders.length > 0;
  const isSelected = selectedId === folder.id;
  const isCurrent = currentFolderId === folder.id;

  return (
    <div>
      <div
        className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-100 ${
          isSelected ? "bg-blue-50 text-blue-700" : ""
        } ${isCurrent ? "cursor-not-allowed opacity-50" : ""}`}
        onClick={() => !isCurrent && onSelect(folder.id)}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(folder.id);
            }}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {folder.expanded ? (
              <FiChevronDown className="h-4 w-4" />
            ) : (
              <FiChevronRight className="h-4 w-4" />
            )}
          </button>
        ) : (
          <div className="w-5" />
        )}
        <FiFolder className="h-4 w-4 flex-shrink-0" />
        <span className="flex-1 truncate text-sm">
          {folder.name}
          {isCurrent && " (current)"}
        </span>
      </div>
      {folder.expanded && hasChildren && (
        <div className="ml-4 border-l border-gray-200 pl-2">
          {childFolders.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={{
                id: child.id,
                name: child.name,
                expanded: expandedFolders.has(child.id),
              }}
              selectedId={selectedId}
              onSelect={onSelect}
              onToggle={onToggle}
              mediaType={mediaType}
              currentFolderId={currentFolderId}
              expandedFolders={expandedFolders}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function MoveMediaModal({
  isOpen,
  onClose,
  mediaIds,
  mediaType,
  currentFolderId,
}: MoveMediaModalProps) {
  // undefined = no selection, null = root, string = folder
  const [selectedFolderId, setSelectedFolderId] = useState<string | null | undefined>(undefined);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const itemCount = mediaIds.length;

  // Query root folders
  const { data: rootData, loading: loadingRoot } = useQuery<
    GetFoldersQuery,
    GetFoldersQueryVariables
  >(GetFoldersDocument, {
    variables: { parentId: null, type: mediaType },
    skip: !isOpen,
  });

  const [bulkMoveMedia, { loading: movingMedia }] = useMutation<
    BulkMoveMediaMutation,
    BulkMoveMediaMutationVariables
  >(BulkMoveMediaDocument, {
    refetchQueries: [
      {
        query: GetMediaDocument,
        variables: { folderId: currentFolderId || undefined, type: mediaType },
      },
    ],
    onCompleted: (data) => {
      const count = data.bulkMoveMedia.length;
      toast.success(`Moved ${count} ${count === 1 ? "item" : "items"}`);
      setError(null);
      onClose();
    },
    onError: (err) => {
      setError(err.message);
      toast.error(`Failed to move: ${err.message}`);
    },
  });

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFolderId(undefined);
      setExpandedFolders(new Set());
      setError(null);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !movingMedia) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, movingMedia, onClose]);

  const handleToggle = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleMove = async () => {
    setError(null);

    if (selectedFolderId === undefined) {
      setError("Please select a destination");
      return;
    }

    // Check if trying to move to current location
    if (selectedFolderId === currentFolderId) {
      setError(
        selectedFolderId === null ? "Media is already at root" : "Media is already in this folder"
      );
      return;
    }

    await bulkMoveMedia({
      variables: {
        ids: mediaIds,
        targetFolderId: selectedFolderId,
      },
    });
  };

  if (!isOpen) return null;

  const rootFolders = rootData?.folders?.edges?.map((e) => e.node) || [];

  return (
    <div
      className="!mt-0 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={movingMedia ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="move-media-title"
    >
      <div
        className="w-full max-w-2xl rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <FiMove className="h-5 w-5 text-gray-600" />
            <h2 id="move-media-title" className="text-lg font-semibold text-gray-900">
              Move Media
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
            disabled={movingMedia}
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="mb-4 text-sm text-gray-600">
            Moving:{" "}
            <span className="font-medium text-gray-900">
              {itemCount} {itemCount === 1 ? "item" : "items"}
            </span>
          </p>

          {/* Folder Tree */}
          <div className="mb-4 max-h-96 overflow-y-auto rounded-md border border-gray-200 bg-white p-3">
            {loadingRoot ? (
              <div className="flex items-center justify-center py-8 text-sm text-gray-500">
                Loading folders...
              </div>
            ) : (
              <div className="space-y-1">
                {/* Root option */}
                <div
                  className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-100 ${
                    selectedFolderId === null ? "bg-blue-50 text-blue-700" : ""
                  } ${currentFolderId === null ? "cursor-not-allowed opacity-50" : ""}`}
                  onClick={() => currentFolderId !== null && setSelectedFolderId(null)}
                >
                  <div className="w-5" />
                  <FiFolder className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 truncate text-sm font-medium">
                    Root
                    {currentFolderId === null && " (current)"}
                  </span>
                </div>

                {/* Folders */}
                {rootFolders.map((folder) => (
                  <FolderTreeItem
                    key={folder.id}
                    folder={{
                      id: folder.id,
                      name: folder.name,
                      expanded: expandedFolders.has(folder.id),
                    }}
                    selectedId={selectedFolderId}
                    onSelect={setSelectedFolderId}
                    onToggle={handleToggle}
                    mediaType={mediaType}
                    currentFolderId={currentFolderId}
                    expandedFolders={expandedFolders}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
              <FiAlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={movingMedia}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleMove}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              disabled={movingMedia || selectedFolderId === undefined}
            >
              {movingMedia ? "Moving..." : "Move"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
